// Ringtone and ringback made with Web Audio (no sound files to load).
// Browsers keep audio locked until the person clicks something on the page,
// so the app unlocks on the first click or key on the page (and from "Turn on").
// Once a page has had a click, the browser lets it resume audio later without
// one, so a ringtone after the computer slept tries to resume first.

type Pattern = { freqs: number[]; onMs: number; offMs: number; gain: number; bursts?: number; gapMs?: number };

const RINGTONE: Pattern = { freqs: [620, 780], onMs: 400, offMs: 1600, gain: 0.18, bursts: 2, gapMs: 200 };
const RINGBACK: Pattern = { freqs: [425], onMs: 1000, offMs: 4000, gain: 0.08 };

export class Sounds {
  private ctx: AudioContext | null = null;
  private stopCurrent: (() => void) | null = null;
  /** bumped by every play/stop: a resume that finishes late must not start an old sound */
  private gen = 0;
  private listeners = new Set<() => void>();

  /** True once the browser lets us play sound. */
  get unlocked(): boolean {
    return this.ctx?.state === 'running';
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Call from a click/keypress handler. */
  async unlock(): Promise<void> {
    try {
      if (!this.ctx) {
        this.ctx = new AudioContext();
        this.ctx.addEventListener('statechange', () => this.listeners.forEach((f) => f()));
      }
      if (this.ctx.state !== 'running') await this.ctx.resume();
    } catch {
      /* no audio device or blocked: the banner stays */
    }
    this.listeners.forEach((f) => f());
  }

  ring(): void {
    this.play(RINGTONE);
  }

  ringback(): void {
    this.play(RINGBACK);
  }

  /** One short, soft two-note tone (PR 3b: "about 1 minute left"); doesn't stop a ringtone. */
  chime(): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const out = ctx.createGain();
    out.gain.value = 0;
    out.connect(ctx.destination);
    const t = ctx.currentTime + 0.02;
    [660, 880].forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      o.connect(out);
      o.start(t + i * 0.22);
      o.stop(t + i * 0.22 + 0.18);
    });
    out.gain.setTargetAtTime(0.06, t, 0.01);
    out.gain.setTargetAtTime(0, t + 0.42, 0.02);
    setTimeout(() => out.disconnect(), 800);
  }

  stop(): void {
    this.gen++;
    this.stopCurrent?.();
    this.stopCurrent = null;
  }

  private play(p: Pattern): void {
    this.stop();
    const ctx = this.ctx;
    if (!ctx) return;
    if (ctx.state !== 'running') {
      // suspended or interrupted (e.g. after sleep): allowed again if the page had a click
      const g = this.gen;
      ctx
        .resume()
        .then(() => {
          if (g === this.gen && ctx.state === 'running') this.start(ctx, p);
        })
        .catch(() => {
          /* still locked: the notification's sound is the alert */
        });
      return;
    }
    this.start(ctx, p);
  }

  private start(ctx: AudioContext, p: Pattern): void {
    const out = ctx.createGain();
    out.gain.value = 0;
    out.connect(ctx.destination);
    const oscs = p.freqs.map((f) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      o.connect(out);
      o.start();
      return o;
    });

    const bursts = p.bursts ?? 1;
    const gap = p.gapMs ?? 0;
    const cycle = (bursts * p.onMs + (bursts - 1) * gap + p.offMs) / 1000;
    let next = ctx.currentTime + 0.02;
    const schedule = () => {
      // keep ~2 cycles scheduled ahead
      while (next < ctx.currentTime + 2 * cycle) {
        let t = next;
        for (let b = 0; b < bursts; b++) {
          out.gain.setTargetAtTime(p.gain, t, 0.01);
          out.gain.setTargetAtTime(0, t + p.onMs / 1000, 0.01);
          t += (p.onMs + gap) / 1000;
        }
        next += cycle;
      }
    };
    schedule();
    const timer = setInterval(schedule, 500);

    this.stopCurrent = () => {
      clearInterval(timer);
      out.gain.cancelScheduledValues(ctx.currentTime);
      out.gain.setTargetAtTime(0, ctx.currentTime, 0.01);
      setTimeout(() => {
        oscs.forEach((o) => o.stop());
        out.disconnect();
      }, 100);
    };
  }
}
